<template>
  <div class="bg-white">
    <div class="mx-auto max-w-6xl px-4 py-12 lg:px-8">
      <h1 class="text-center text-3xl font-semibold text-ink sm:text-4xl">Frequently Asked Questions</h1>
      <div class="mt-8 grid gap-6 lg:grid-cols-[240px_1fr] lg:items-start">
        <aside class="hidden self-start rounded-3xl bg-white p-4 shadow-[0_15px_60px_rgba(0,0,0,0.08)] ring-1 ring-gray-100 lg:block">
          <div class="space-y-2 text-sm font-semibold text-muted">
            <button
              v-for="category in categories"
              :key="category.id"
              class="flex w-full items-center justify-between rounded-xl px-3 py-2 transition"
              :class="activeCategory === category.id ? 'bg-gray-900 text-white shadow-lg' : 'hover:bg-gray-50 text-ink'"
              @click="setCategory(category.id)"
            >
              <span>{{ category.title }}</span>
            </button>
          </div>
        </aside>

        <main class="space-y-4">
          <div
            v-for="qa in filteredFaqs"
            :key="qa.id"
            class="overflow-hidden rounded-2xl bg-gray-50 text-ink shadow-card transition hover:shadow-lg"
          >
            <button
              class="flex w-full items-center justify-between px-4 py-4 text-left text-sm font-semibold sm:text-base"
              @click="toggleFaq(qa.id)"
            >
              <span>{{ qa.question }}</span>
              <i :class="['ri-arrow-down-s-line text-lg transition', openFaq === qa.id ? 'rotate-180 text-brand' : '']"></i>
            </button>
            <div v-if="openFaq === qa.id" class="border-t border-gray-100 bg-white px-4 py-4 text-sm text-muted">
              <p>{{ qa.answer }}</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const categories = [
  { id: 'face', title: 'Face Recognition' },
  { id: 'photos', title: 'Photos on Invi8' },
  { id: 'create', title: 'Create Event' },
  { id: 'pricing', title: 'Pricing' },
  { id: 'sell', title: 'Sell Photos' },
  { id: 'privacy', title: 'Privacy' },
  { id: 'other', title: 'Others' }
]

const faqs = [
  {
    id: 'face-accuracy',
    category: 'face',
    question: "How accurate is Invi8's facial recognition?",
    answer: 'We use a 99%+ accuracy model, optimized for crowds, side profiles, and low-light shots.'
  },
  {
    id: 'face-works',
    category: 'face',
    question: 'How does facial recognition work for guests?',
    answer: 'Guests upload a selfie once; we match it across uploaded event photos, then notify them securely.'
  },
  {
    id: 'face-guests',
    category: 'face',
    question: 'How many guests can find their photos?',
    answer: 'We support thousands of guests per event with batch processing and smart queueing.'
  },
  {
    id: 'photos-storage',
    category: 'photos',
    question: 'Where are my photos stored?',
    answer: 'Photos are stored in secure cloud storage with encryption at rest and in transit.'
  },
  {
    id: 'event-setup',
    category: 'create',
    question: 'How do I create an event?',
    answer: 'Choose an invite template, set dates, upload media, and publish—everything is guided in the app.'
  },
  {
    id: 'pricing',
    category: 'pricing',
    question: 'How is pricing handled?',
    answer: 'Hosts can set free or paid access; vendors can list packages and add-ons with clear taxes and fees.'
  },
  {
    id: 'sell-photos',
    category: 'sell',
    question: 'Can I sell photos to guests?',
    answer: 'Yes—set pricing, enable downloads, and we handle payments and delivery links.'
  },
  {
    id: 'privacy-guest',
    category: 'privacy',
    question: 'How do you protect guest privacy?',
    answer: 'We minimize data, honor deletion/export requests, and restrict matches to event-specific photos.'
  },
  {
    id: 'other-support',
    category: 'other',
    question: 'Need more help?',
    answer: 'Contact support@invi8.com or reach out via the contact page for detailed guidance.'
  }
]

const activeCategory = ref('face')
const openFaq = ref('face-accuracy')

const setCategory = (id) => {
  activeCategory.value = id
  const first = faqs.find((item) => item.category === id)
  openFaq.value = first ? first.id : ''
}

const toggleFaq = (id) => {
  openFaq.value = openFaq.value === id ? '' : id
}

const filteredFaqs = computed(() => faqs.filter((qa) => qa.category === activeCategory.value))
</script>
